import { fetchWithToken } from "../contexts/AuthContext";
import config from "../config";

const API = config.apiUrl;

// organizations (master admin)

export async function fetchOrganizations(params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.search) query.set("search", params.search);

  const url = `${API}/api/admin/organizations${query.toString() ? `?${query}` : ""}`;
  const res = await fetchWithToken(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function fetchOrganization(orgId) {
  const res = await fetchWithToken(`${API}/api/admin/organizations/${orgId}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createOrganization(data) {
  const res = await fetchWithToken(`${API}/api/admin/organizations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateOrganization(orgId, data) {
  const res = await fetchWithToken(`${API}/api/admin/organizations/${orgId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function bulkUpdateOrganizations(ids, isActive) {
  const res = await fetchWithToken(`${API}/api/admin/organizations/bulk`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, is_active: isActive }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// users (master admin)

export async function fetchPlatformActivity(params = {}) {
  const query = new URLSearchParams();
  if (params.type) query.set("type", params.type);
  if (params.page) query.set("page", params.page);
  if (params.per_page) query.set("per_page", params.per_page);
  if (params.sort_by) query.set("sort_by", params.sort_by);
  if (params.sort_dir) query.set("sort_dir", params.sort_dir);
  if (params.status) query.set("status", params.status);
  if (params.side) query.set("side", params.side);
  if (params.search) query.set("search", params.search);
  if (params.date_from) query.set("date_from", params.date_from);
  if (params.date_to) query.set("date_to", params.date_to);

  const url = `${API}/api/admin/platform-activity${query.toString() ? `?${query}` : ""}`;
  const res = await fetchWithToken(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function fetchUsers(params = {}) {
  const query = new URLSearchParams();
  if (params.organization_id)
    query.set("organization_id", params.organization_id);
  if (params.type) query.set("type", params.type);
  if (params.status) query.set("status", params.status);
  if (params.search) query.set("search", params.search);

  const url = `${API}/api/admin/users${query.toString() ? `?${query}` : ""}`;
  const res = await fetchWithToken(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function fetchUser(userId) {
  const res = await fetchWithToken(`${API}/api/admin/users/${userId}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createUser(data) {
  const res = await fetchWithToken(`${API}/api/admin/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function generateUserForOrg(
  organizationId,
  isCompanyAdmin = false,
  permissions = null,
) {
  const res = await fetchWithToken(`${API}/api/admin/users/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      organization_id: organizationId,
      is_company_admin: isCompanyAdmin,
      permissions,
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateUser(userId, data) {
  const res = await fetchWithToken(`${API}/api/admin/users/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function resetUserPassword(userId, password) {
  const res = await fetchWithToken(
    `${API}/api/admin/users/${userId}/reset-password`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    },
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function promoteUser(userId) {
  const res = await fetchWithToken(`${API}/api/admin/users/${userId}/promote`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function demoteUser(userId) {
  const res = await fetchWithToken(`${API}/api/admin/users/${userId}/demote`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function bulkUpdateUsers(ids, isActive) {
  const res = await fetchWithToken(`${API}/api/admin/users/bulk`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, is_active: isActive }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// materials (master admin)

export async function fetchMaterials() {
  const res = await fetchWithToken(`${API}/api/admin/materials`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createMaterialEntry(data) {
  const res = await fetchWithToken(`${API}/api/admin/materials`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateMaterialEntry(data) {
  const res = await fetchWithToken(`${API}/api/admin/materials/update`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteMaterialEntry(name) {
  const res = await fetchWithToken(`${API}/api/admin/materials/${encodeURIComponent(name)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function toggleMaterialActive(name, active) {
  const res = await fetchWithToken(`${API}/api/admin/materials/${encodeURIComponent(name)}/toggle`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ active }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// accounts (company admin - own org)

export async function fetchOrgInfo() {
  const res = await fetchWithToken(`${API}/api/accounts/organization`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function fetchAccounts(params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.search) query.set("search", params.search);

  const url = `${API}/api/accounts${query.toString() ? `?${query}` : ""}`;
  const res = await fetchWithToken(url);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function fetchAccount(userId) {
  const res = await fetchWithToken(`${API}/api/accounts/${userId}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function createAccount(data) {
  const res = await fetchWithToken(`${API}/api/accounts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function generateTempAccount(permissions = null) {
  const res = await fetchWithToken(`${API}/api/accounts/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ permissions }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateAccount(userId, data) {
  const res = await fetchWithToken(`${API}/api/accounts/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function resetAccountPassword(userId, password) {
  const res = await fetchWithToken(
    `${API}/api/accounts/${userId}/reset-password`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    },
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function promoteAccount(userId) {
  const res = await fetchWithToken(`${API}/api/accounts/${userId}/promote`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function bulkUpdateAccounts(ids, isActive) {
  const res = await fetchWithToken(`${API}/api/accounts/bulk`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, is_active: isActive }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
